import { useState } from 'react';
import { Icon } from '../common';
import { useClickOutside } from '../../lib/useClickOutside';
import { useCreateModal, type CreateKind } from '../CreateModalProvider';
import { useAccountModal } from '../account/AccountModalProvider';

// The variable pieces that slot into the global top bar (app-shell-redesign):
// the one global search (AC3), the teal New menu (AC5), and the placeholder
// notification bell (AC6). Each dismisses on outside-click; the New menu drives
// the global CreateModalProvider so a create opens over the current page.

// AC5 — the "Create" set. Dog is deliberately omitted (no standalone dog-create
// flow yet; a dog is created inside an owner). Booking/owner reuse the owner-
// booking modal; helper/leave reuse the staff modal.
const CREATE_ITEMS: { kind: CreateKind; label: string; icon: string }[] = [
  { kind: 'booking', label: 'New booking', icon: 'calendar' },
  // A VIEWING SITS BESIDE A BOOKING, second, because it is the thing that comes
  // BEFORE one — somebody who exists before a household does. It is logged with
  // the caller still on the telephone, from whatever page happens to be open.
  { kind: 'viewing', label: 'New viewing', icon: 'eye' },
  { kind: 'owner', label: 'New owner', icon: 'paw' },
  { kind: 'helper', label: 'New helper', icon: 'clock' },
  { kind: 'leave', label: 'Log leave', icon: 'calendar-today' },
];

export function NewMenu({ isAdmin = false }: { isAdmin?: boolean }) {
  const create = useCreateModal();
  const account = useAccountModal();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
  // No provider (staff-only shell) → nothing to create, so no button.
  if (!create) return null;
  return (
    <div className="tb-new" ref={ref}>
      <button
        type="button"
        className="tb-new-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="plus" size={15} />
        <span className="tb-new-label">New</span>
        <Icon name="chevron-down" size={14} className="tb-new-caret" />
      </button>
      {open && (
        <div className="popover tb-menu tb-new-menu" role="menu">
          <div className="tb-menu-head">Create</div>
          {CREATE_ITEMS.map((it) => (
            <button
              key={it.kind}
              type="button"
              role="menuitem"
              className="tb-menu-item"
              onClick={() => {
                setOpen(false);
                create.openCreate(it.kind);
              }}
            >
              <Icon name={it.icon} size={15} />
              <span>{it.label}</span>
            </button>
          ))}
          {/* New user — access management, admins only (opens the global
              add-user modal). Separated from the operational creates above. */}
          {isAdmin && account && (
            <>
              <div className="tb-menu-sep" role="separator" />
              <button
                type="button"
                role="menuitem"
                className="tb-menu-item"
                onClick={() => {
                  setOpen(false);
                  account.openUserCreate();
                }}
              >
                <Icon name="user" size={15} />
                <span>New user</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// AC6 — a bell with a placeholder popover; no live content this build. It's chrome
// now so the layout and the "you have somewhere for alerts" affordance are real
// before the content story lands.
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
  return (
    <div className="tb-bell" ref={ref}>
      <button
        type="button"
        className="tb-icon-btn"
        aria-label="Notifications"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="bell" size={18} />
      </button>
      {open && (
        <div className="popover tb-menu tb-bell-pop" role="dialog" aria-label="Notifications">
          <div className="tb-menu-head">Notifications</div>
          <div className="tb-bell-empty">Nothing new.</div>
        </div>
      )}
    </div>
  );
}

// AC3 — the ONE global search now lives in components/search/GlobalSearch.tsx
// (the real unified search). This module keeps the New menu + notification bell.
